import React, { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import cloneDeep from 'lodash.clonedeep'

import { usePagination } from '@hooks/usePagination'
import { useModal } from '@hooks/contexts/ModalContext/ModalContext'
import { useAuthentication } from '@hooks/useAuthentication'
import { useAlert } from '@hooks/useAlert'
import { FollowSuccessStatusEnum } from '@interfaces/FollowSuccessStatusEnum'
import { PenNameByUserResponse } from '@models/penName/PenNameByUserResponse'
import { LikeSuccessStatusEnum } from '@models/book/LikeSuccessStatusEnum'
import { useTab } from '@components/Tabs/Tabs'
import { scrollToElement } from '@lib/utils'
import { useBookAction } from '@hooks/book/useBookAction'
import { useUserAction } from '@hooks/user/useUserAction'
import { usePenNameAction } from '@hooks/penName/usePenNameAction'
import { PenNameTabAcceptProps, PenNameTabProps } from './interface'

export function withPenNameTab(Component: React.FC<PenNameTabProps>) {
  function WithPenNameTab({ userId }: PenNameTabAcceptProps) {
    const bookClient = useBookAction()
    const penNameClient = usePenNameAction()
    const userClient = useUserAction()
    const queryClient = useQueryClient()
    const { page, perpage, pageChange } = usePagination()
    const { handleTabNameChange } = useTab()
    const { user, isAuthenticated, canFetchApi } = useAuthentication()
    const [searchText, setSearchText] = useState('')
    const loginModal = useModal({ modal: 'login' })
    const alert = useAlert()

    const { data: penName, isLoading } = useQuery(
      ['pen-name-by-user', userId, searchText, page, perpage],
      () => {
        return penNameClient.getPenNameByUserId({
          id: userId,
          searchText,
          page,
          perpage,
        })
      },
      {
        enabled: !!userId && canFetchApi,
      }
    )

    function handleSearchChange(value: string) {
      pageChange(1)
      setSearchText(value)
    }

    async function handleFollowPenName(penNameId: number) {
      if (isAuthenticated) {
        try {
          const type = await userClient.toggleFollowPenName(penNameId)
          queryClient.setQueryData<PenNameByUserResponse | undefined>(
            ['pen-name-by-user', userId, searchText, page, perpage],
            (oldData?: PenNameByUserResponse) => {
              if (oldData) {
                const dataClone = cloneDeep(oldData)
                const penNameIndex = dataClone.data.findIndex(
                  item => item.id === penNameId
                )
                dataClone.data[penNameIndex].isFollowed =
                  type === FollowSuccessStatusEnum.CREATE
                dataClone.data[penNameIndex].totalFollower =
                  type === FollowSuccessStatusEnum.CREATE
                    ? dataClone.data[penNameIndex].totalFollower + 1
                    : dataClone.data[penNameIndex].totalFollower - 1

                return dataClone
              }
              return oldData
            }
          )
          if (type === FollowSuccessStatusEnum.CREATE) {
            alert.success('ติดตามนามปากกาสำเร็จ')
          } else {
            alert.success('เลิกติดตามนามปากกาสำเร็จ')
          }
        } catch (_) {
          alert.error('เกิดข้อผิดพลาดในการติดตามนามปากกา')
        }
      } else {
        loginModal.show()
      }
    }

    const { mutate: toggleBookLike, isLoading: isLoadingToggleBookLike } =
      useMutation(
        ({ bookId }: { penNameId: number; bookId: number }) =>
          bookClient.toggleBookLike(bookId),
        {
          onSuccess: (type, { penNameId, bookId }) => {
            queryClient.setQueryData<PenNameByUserResponse | undefined>(
              ['pen-name-by-user', userId, searchText, page, perpage],
              (oldData?: PenNameByUserResponse) => {
                if (oldData) {
                  const dataClone = cloneDeep(oldData)
                  const penNameIndex = dataClone.data.findIndex(
                    item => item.id === penNameId
                  )
                  const { books } = dataClone.data[penNameIndex]
                  const bookIndex = books.findIndex(item => item.id === bookId)
                  dataClone.data[penNameIndex].books[bookIndex].isLiked =
                    type === LikeSuccessStatusEnum.CREATE
                  dataClone.data[penNameIndex].books[bookIndex].likeCount =
                    type === LikeSuccessStatusEnum.CREATE
                      ? books[bookIndex].likeCount + 1
                      : Math.min(0, books[bookIndex].likeCount - 1)

                  return dataClone
                }
                return oldData
              }
            )
          },
        }
      )

    async function handleLikeBook(penNameId: number, bookId: number) {
      if (isAuthenticated) {
        try {
          await toggleBookLike({
            penNameId,
            bookId,
          })
        } catch (_) {
          alert.error('เกิดข้อผิดพลาดในการเชื่อมต่อ กรุณาลองอีกครั้ง')
        }
      } else {
        loginModal.show()
      }
    }

    function handlePaginationChange(newPage: number) {
      pageChange(newPage)
      scrollToElement('#profile')
    }

    useEffect(() => {
      if (penName) {
        handleTabNameChange(
          'penName',
          `นามปากกา (${penName.totalPenName.toLocaleString()})`
        )
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [penName])

    const componentProps = {
      userId,
      penNameList: penName?.data ?? [],
      page,
      perpage,
      total: penName?.total || 0,
      canFollow: userId !== user.id,
      handlePaginationChange,
      handleSearchChange,
      handleFollowPenName,
      handleLikeBook,
      isLoading,
      isLoadingToggleBookLike,
    }
    return <Component {...componentProps} />
  }
  return WithPenNameTab
}
